import { ColorsType, tokens } from '@news-mono/design-tokens'
import { Product } from '@news-mono/web-common'
import { FallbackImages } from '../__fallback-images'
import { Fonts, fonts } from '../__styling/settings/fonts'
import {
    CardMargins,
    ComponentMetrics,
    metrics,
    SiteMetrics,
    ThemeMargins,
} from '../__styling/settings/metrics'
import {
    get190YearsThemeValues,
    getBestAustralianYarnTheme,
    getCNBCThemeValues,
    getCommonwealthGamesValues,
    getDevelopingNewsTheme,
    getGoMatildasTheme,
    getGrandFinalFreoValues,
    getGrandFinalThemeValues,
    getKingsCoronationTheme,
    getStreamerScreamerThemeValues,
    getTheEconomistThemeValues,
    getTheNightlyCurationThemeValues,
} from './feature-theme-values'
import { PNBorderRadius } from './settings/borderRadius'
import { BaseSectionValues, sections } from './settings/sections'
import { PerthNowSectionValues } from './settings/sections/perthnow'
import { SevenNewsSectionValues } from './settings/sections/sevennews'
import { TheWestSectionValues } from './settings/sections/thewest'
import {
    CommonZIndex,
    PerthNowZindex,
    SevenNewsZindex,
    TheWestZindex,
    TheNightlyZindex,
    zIndex,
} from './settings/z-index'
import { TheNightlySectionValues } from './settings/sections/thenightly'
import { LunaTheme } from '../compositions/Luna/Luna.theme'
import { useTheme } from '@emotion/react'
import { BreakpointKeys, NightlyBreakpointKeys } from './settings/breakpoints'
import {
    sevenNewsLunaTheme,
    theNightlyLunaTheme,
} from '../compositions/Luna/Luna.theme'
import { DeepPartial } from '../../../design-tokens/src/types/types'
import {
    TagLinkNavigationTheme,
    theNightlyTagLinkNavigationTheme,
} from '../navigation/TNTagLinkNavigation/TagLinkNavigation.theme'
import {
    perthNowSectionHeaderTheme,
    SectionHeaderTheme,
    sevenNewsSectionHeaderTheme,
    theNightlySectionHeaderTheme,
    theWestSectionHeaderTheme,
} from '../section-header/SectionHeader/SectionHeader.theme'
import {
    perthNowTextMixinTheme,
    PerthNowTextTypes,
    sevenNewsTextMixinTheme,
    SevenNewsTextTypes,
    TextMixinTheme,
    theNightlyTextMixinTheme,
    TheNightlyTextTypes,
    theWestTextMixinTheme,
    TheWestTextTypes,
} from '../mixins/TextMixin/TextMixin.theme'
import {
    TheNightlyBreadcrumbTheme,
    thenightlyBreadcrumbTheme,
} from '../navigation/TheNightlyBreadcrumb/TheNightlyBreadcrumb.theme'
import {
    ButtonTheme,
    theNightlyButtonTheme,
} from '../buttons/NightlyButton/DefaultButton/DefaultButton.theme'
import {
    SectionContentTheme,
    theNightlySectionContentTheme,
} from '../compositions/TheNightly/SectionContent/SectionContent.theme'
import {
    ArticleCardListTheme,
    theNightlyArticleCardListTheme,
} from '../collections/TheNightly/ArticleCardList.theme'
import {
    SharingTheme,
    theNightlySharingTheme,
    sevenNewsSharing,
} from '../buttons/TheNightlySharing/Sharing.theme'
import {
    TheNightlyAppDLCardTheme,
    theNightlyAppDLCardTheme,
} from '../cards/TNAppDownloadCard/TNAppDownloadCard.theme'
import {
    PullquoteTheme,
    theNightlyPullquoteTheme,
} from '../publication/Pullquote/Pullquote.theme'
import { MarsTheme, theNightlyMarsTheme } from '../compositions/Mars/Mars.theme'
import {
    BioBlockTheme,
    perthNowBioBlockTheme,
    sevenNewsBioBlockTheme,
    theNightlyBioBlockTheme,
    theWestBioBlockTheme,
} from '../content/BioBlock/BioBlock.theme'
import {
    ProfileEmailButtonTheme,
    perthNowProfileEmailButtonTheme,
    theNightlyProfileEmailButtonTheme,
    theWestProfileEmailButtonTheme,
} from '../buttons/ProfileEmailButton/ProfileEmailButton.theme'
import {
    LandscapeNightlyTheme,
    theNightlyLandscapeTheme,
} from '../cards/LandscapeNightly/LandscapeNightly.theme'
import {
    NewsletterTheme,
    perthNowNewsletterTheme,
    theNightlyNewsletterTheme,
    theWestNewsletterTheme,
} from '../banners/NewsletterSignup/NewsletterSignup.theme'
import {
    PaginationTheme,
    perthNowPaginationTheme,
    sevenNewsPaginationTheme,
    theNightlyPaginationTheme,
    theWestPaginationTheme,
} from '../data-controllers/Pagination/Pagination.theme'
import {
    perthNowStickyAccountBannerTheme,
    StickyAccountBannerTheme,
    theNightlyStickyAccountBannerTheme,
} from '../user-registration/warden/sticky-account-banner/components/StickyAccountInnerBanner.theme'
import {
    theNightlyTrendingArticlesTheme,
    TrendingArticlesTheme,
} from '../content/TrendingArticles/TrendingArticles.theme'
import {
    NotificationCentreTheme,
    theNightlyNotificationCentreTheme,
} from '../notification-centre/NotificationCentre.theme'
import {
    ElectionWidgetTheme,
    defaultElectionWidgetTheme,
    theWestElectionWidgetTheme,
    sevenNewsElectionWidgetTheme,
} from '../widgets/election/ElectionWidgetTheme.theme'
import {
    ElectionTppEstCardTheme,
    theNightlyElectionTppEstCardTheme,
    theWestElectionTppEstCardTheme,
    sevenNewsElectionTppEstCardTheme,
} from '../widgets/election/ElectionTppEstCard/ElectionTppEstCard.theme'

// -------------------------------------------------------------------------------------
// Before making changes to this file, it is recommended to read through:
// https://wanews.atlassian.net/wiki/spaces/REF/pages/153714860/Theming+Restructure
// To gain an understanding of the terminology and responsibilities of types and files.
//
// There is also a codetour named 'Theming Restructure' which explains how things work.
// -------------------------------------------------------------------------------------

/**
 * This type groups Theme config related types together.
 * I.e. it lets you specify what breakpoints, or textTypes a theme can use.
 *
 * This is a broad type, and not intended for direct use, other than for constraining generic parameters.
 * I.e. coolThemeFunction<T extends ThemeType> ...
 *
 * When declaring themes for components, the masthead-specific theme configs, i.e. TheWestThemeConfig should
 * be used, as they provide autocomplete, and correct values for breakpoints, etc.
 */
export type ThemeConfig = {
    breakpoints: BreakpointKeys
    textType: string
    sectionValues: BaseSectionValues
}

/**
 * A helper type to help get autocomplete when defining Theme Configs.
 */
export type BuildThemeConfig<T extends ThemeConfig> = T

/**
 * The generic theme type that is passed down to components.
 *
 * NEVER PUT MASTHEAD-SPECIFIC VALUES IN HERE.
 *
 * Refer to the following documentation:
 * https://wanews.atlassian.net/wiki/spaces/REF/pages/153714860/Theming+Restructure
 * and do consider the necessity of adding values to this.
 *
 * Regarding <T extends ThemeConfig = any>, Since this isn't used directly to declare types,
 * it's safe to have this as any, so that any Theme<ArbitraryConfig> can be assigned to Theme for utility functions.
 * (And backwards compatibility as having this as ThemeConfig causes the mother of all type-errors.)
 */
export interface Theme<T extends ThemeConfig = any> {
    kind: Product // TODO: remove usages. This may take a while.
    fallbackImages: FallbackImages // ! DEAD CODE - All are 'undefined as any'.
    margins: ThemeMargins // TODO: Can be moved into ThemeConfig as 'metrics'.
    /** @deprecated replaced by component-themes */
    cards: CardMargins // TODO: Should be replaced with component-themes and margins.
    /** @deprecated replaced by component-themes */
    componentMetrics: ComponentMetrics // TODO: Replace with component-themes.
    /** @deprecated Theme should not be used to figure out the current section. */
    section: string

    zIndex: (
        | // Can be added to ThemeConfig for better intellisense. Might be replaced by component-themes.
        TheWestZindex
        | PerthNowZindex
        | SevenNewsZindex
        | TheNightlyZindex
    ) &
        CommonZIndex
    baseFontSize: number
    colors: ColorsType // Can be improved via ThemeConfig. Many values may need to be moved into component-themes.
    radius?: PNBorderRadius // Should not be masthead specific.
    siteMetrics: SiteMetrics
    fonts: Fonts
    sectionValues: T['sectionValues']

    // New additions should be made with the following pattern:
    // Component tokens are defined in the component's .theme file.
    // Will prevent dead theme tokens when components are removed/modified.
    components: ThemeFunction<Partial<ComponentThemes<T>>, T>
}

/** A type used for declaring scheme-token overrides. */
export type PartialTheme<T extends ThemeConfig> = DeepPartial<Theme<T>>

/**
 * The generic component theme type.
 *
 * When adding new component themes, they should be added here.
 */
type ComponentThemes<T extends ThemeConfig> = {
    luna: LunaTheme<T>
    sectionHeader: SectionHeaderTheme<T>
    textMixin: TextMixinTheme<T>
    nightlyBreadcrumb: TheNightlyBreadcrumbTheme<T>
    buttonTheme: ButtonTheme<T>
    sectionContent: SectionContentTheme<T>
    articleCardList: ArticleCardListTheme<T>
    sharing: SharingTheme<T>
    nightlyAppDLCard: TheNightlyAppDLCardTheme<T>
    pullQuote: PullquoteTheme<T>
    mars: MarsTheme<T>
    bioBlock: BioBlockTheme<T>
    profileEmailButton: ProfileEmailButtonTheme<T>
    landscape: LandscapeNightlyTheme<T>
    taglinkNav: TagLinkNavigationTheme<T>
    newsletter: NewsletterTheme<T>
    pagination: PaginationTheme<T>
    stickyAccountBanner: StickyAccountBannerTheme<T>
    trendingArticles: TrendingArticlesTheme<T>
    notificationCentre: NotificationCentreTheme<T>
    electionTheme?: ElectionWidgetTheme<T>
    electionTppEstCardTheme?: ElectionTppEstCardTheme<T>
}

/** A type used for declaring component theme overrides. */
export type PartialComponentTheme<T extends ThemeConfig> = DeepPartial<
    ComponentThemes<T>
>

type ComponentThemeId = keyof ComponentThemes<ThemeConfig>

/** Helper type to ensure that ThemeConfig is passed into components. */
type ComponentTheme<T extends ThemeConfig> = {}

/** A function that maintains the 'this' value as the theme. */
export type ThemeFunction<
    U extends ComponentTheme<T>,
    T extends ThemeConfig,
> = (this: Theme<T>) => U

// Register the theme type to emotion (the styled-components library).
type EmotionTheme = Theme
declare module '@emotion/react' {
    export interface Theme extends EmotionTheme {}
}

// TODO: Investigate this if this is dead.
export interface ThemeProps {
    theme: Theme
}

/** @deprecated use Scheme and Component overrides instead. */
export type ThemeOverrideTypes =
    | 'tokyo'
    | 'grand-final'
    | 'cnbc'
    | 'lifestyle'
    | 'entertainment'
    | 'sport'
    | 'tech'
    | 'politics'
    | 'business'
    | 'bauhaus-default'
    | 'world'
    | 'spotlight'
    | 'commonwealth-games'
    | 'grand-final-freo'
    | 'developing-news'
    | 'developing-news-teal'
    | 'developing-news-blue'
    | 'developing-news-black'
    | 'developing-news-white'
    | 'developing-news-australia'
    | 'best-australian-yarn'
    | 'streamer-screamer'
    | 'the-economist'
    | 'cnbcEconomist'
    | 'kings-coronation'
    | '190-years-of-the-west'
    | 'best-picks'
    | 'go-matildas'
    | 'best-australian-short-film'
    | 'thenightly-curation'

interface getThemeOverrideValuesProps {
    override: ThemeOverrideTypes
    product: Product
}

// Helper functions.

/** @deprecated Use scheme and component theme overrides. */
export function getThemeOverrideValues({
    override,
    product,
}: getThemeOverrideValuesProps) {
    switch (override) {
        case 'go-matildas':
            return getGoMatildasTheme()
        case 'grand-final':
            return getGrandFinalThemeValues({ product })
        case 'cnbc':
            return getCNBCThemeValues() //This is only active on The West
        case 'cnbcEconomist':
            return getCNBCThemeValues()
        case 'the-economist':
            return getTheEconomistThemeValues()
        case 'kings-coronation':
            return getKingsCoronationTheme()
        case 'commonwealth-games':
            return getCommonwealthGamesValues()
        case 'grand-final-freo':
            return getGrandFinalFreoValues()
        case 'developing-news':
            return getDevelopingNewsTheme('grey')
        case 'developing-news-teal':
            return getDevelopingNewsTheme('teal')
        case 'developing-news-blue':
            return getDevelopingNewsTheme('blue')
        case 'developing-news-black':
            return getDevelopingNewsTheme('black')
        case 'developing-news-white':
            return getDevelopingNewsTheme('white')
        case 'developing-news-australia':
            return getDevelopingNewsTheme('australia')
        case 'best-australian-yarn':
            return getBestAustralianYarnTheme()
        case 'streamer-screamer':
            return getStreamerScreamerThemeValues()
        case '190-years-of-the-west':
            return get190YearsThemeValues()
        case 'thenightly-curation':
            return getTheNightlyCurationThemeValues()

        default:
            return {}
    }
}

/**
 * Returns the theme object for a component.
 * Throws an error if the theme declaration does not exist.
 */
export const getComponentTheme = <T extends ComponentThemeId>(
    theme: Theme,
    componentThemeId: T,
): NonNullable<ComponentThemes<ThemeConfig>[T]> => {
    const componentTheme = theme.components()[componentThemeId]

    if (!componentTheme) {
        throw new Error(
            `No component theme definition made for ${componentThemeId} in ${theme.kind} theme!`,
        )
    }
    return componentTheme
}

/**
 * A reactive hook for the theme for a component.
 * Throws an error if the theme declaration does not exist.
 */
export const useComponentTheme = <T extends ComponentThemeId>(
    componentThemeId: T,
): NonNullable<ComponentThemes<ThemeConfig>[T]> => {
    const theme = useTheme()

    return getComponentTheme(theme, componentThemeId)
}

// TODO: The following may be broken out into their own files at some point.

// The West Australian.

export type TheWestThemeConfig = BuildThemeConfig<{
    breakpoints: BreakpointKeys
    textType: TheWestTextTypes
    sectionValues: TheWestSectionValues
}>

export type TheWestTheme = Theme<TheWestThemeConfig>

export const thewest: TheWestTheme = {
    kind: Product.TheWest,
    fonts: fonts.thewest,
    margins: metrics.thewest.margins,
    cards: metrics.thewest.cards,
    componentMetrics: metrics.thewest.componentMetrics,
    siteMetrics: metrics.thewest.siteMetrics,
    zIndex: { ...zIndex.thewest, ...zIndex.common },

    // This following properties are provided through route resolution in provide-route-resolved-data-to-theme.tsx
    fallbackImages: undefined as any,
    section: 'default',
    sectionValues: sections.thewest.default,
    baseFontSize: metrics.thewest.baseFontSize,
    colors: tokens.thewest.colors,

    components() {
        return {
            sectionHeader: theWestSectionHeaderTheme(this),
            textMixin: theWestTextMixinTheme(this),
            bioBlock: theWestBioBlockTheme(this),
            profileEmailButton: theWestProfileEmailButtonTheme(this),
            newsletter: theWestNewsletterTheme(this),
            pagination: theWestPaginationTheme(this),
            electionTheme: theWestElectionWidgetTheme(this),
            electionTppEstCardTheme: theWestElectionTppEstCardTheme(this),
        }
    },
}

export type PerthNowThemeConfig = BuildThemeConfig<{
    breakpoints: BreakpointKeys
    textType: PerthNowTextTypes
    sectionValues: PerthNowSectionValues
}>

// PerthNow.

export type PerthNowTheme = Theme<PerthNowThemeConfig>

export const perthnow: PerthNowTheme = {
    kind: Product.PerthNow,
    fonts: fonts.perthnow,
    margins: metrics.perthnow.margins,
    cards: metrics.perthnow.cards,
    componentMetrics: metrics.perthnow.componentMetrics,
    siteMetrics: metrics.perthnow.siteMetrics,
    zIndex: { ...zIndex.perthnow, ...zIndex.common },

    // This following properties are provided through route resolution in provide-route-resolved-data-to-theme.tsx
    fallbackImages: undefined as any,
    section: 'default',
    sectionValues: sections.perthnow.default,
    baseFontSize: metrics.perthnow.baseFontSize,
    colors: tokens.perthnow.colors,

    components() {
        return {
            sectionHeader: perthNowSectionHeaderTheme(this),
            textMixin: perthNowTextMixinTheme(this),
            bioBlock: perthNowBioBlockTheme(this),
            profileEmailButton: perthNowProfileEmailButtonTheme(this),
            newsletter: perthNowNewsletterTheme(this),
            pagination: perthNowPaginationTheme(this),
            stickyAccountBanner: perthNowStickyAccountBannerTheme(this),
            electionTheme: defaultElectionWidgetTheme(this),
        }
    },
}

// SevenNews.

export type SevenNewsThemeConfig = BuildThemeConfig<{
    breakpoints: BreakpointKeys
    textType: SevenNewsTextTypes
    sectionValues: SevenNewsSectionValues
}>

export type SevenNewsTheme = Theme<SevenNewsThemeConfig>

export const sevennews: SevenNewsTheme = {
    kind: Product.SevenNews,
    fonts: fonts.sevennews,
    margins: metrics.sevennews.margins,
    cards: metrics.sevennews.cards,
    componentMetrics: metrics.sevennews.componentMetrics,
    siteMetrics: metrics.sevennews.siteMetrics,
    zIndex: { ...zIndex.sevennews, ...zIndex.common },

    // This following properties are provided through route resolution in provide-route-resolved-data-to-theme.tsx
    fallbackImages: undefined as any,
    section: 'default',
    sectionValues: sections.sevennews.default,
    baseFontSize: metrics.sevennews.baseFontSize,
    colors: tokens.sevennews.colors,

    components() {
        return {
            sectionHeader: sevenNewsSectionHeaderTheme,
            textMixin: sevenNewsTextMixinTheme(this),
            bioBlock: sevenNewsBioBlockTheme(this),
            pagination: sevenNewsPaginationTheme(this),
            electionTheme: sevenNewsElectionWidgetTheme(this),
            electionTppEstCardTheme: sevenNewsElectionTppEstCardTheme(this),
            sharing: sevenNewsSharing,
            luna: sevenNewsLunaTheme,
        }
    },
}

// TheNightly.

export type TheNightlyThemeConfig = BuildThemeConfig<{
    breakpoints: NightlyBreakpointKeys
    textType: TheNightlyTextTypes
    sectionValues: TheNightlySectionValues
}>

export type TheNightlyTheme = Theme<TheNightlyThemeConfig>

export const thenightly: TheNightlyTheme = {
    kind: Product.TheNightly,
    // Todo: update this when available
    fonts: fonts.thenightly,
    margins: metrics.thenightly.margins,
    cards: metrics.thenightly.cards,
    componentMetrics: metrics.thenightly.componentMetrics,
    siteMetrics: metrics.thenightly.siteMetrics,
    zIndex: { ...zIndex.thenightly, ...zIndex.common },

    // This following properties are provided through route resolution in provide-route-resolved-data-to-theme.tsx
    fallbackImages: undefined as any,
    section: 'default',
    sectionValues: sections.thenightly.default,
    baseFontSize: metrics.thenightly.baseFontSize,
    colors: tokens.thenightly.colors,

    components() {
        return {
            luna: theNightlyLunaTheme,
            textMixin: theNightlyTextMixinTheme(this),
            sectionHeader: theNightlySectionHeaderTheme(this),
            nightlyBreadcrumb: thenightlyBreadcrumbTheme(this),
            sharing: theNightlySharingTheme,
            pullQuote: theNightlyPullquoteTheme(this),
            buttonTheme: theNightlyButtonTheme(this),
            sectionContent: theNightlySectionContentTheme(this),
            articleCardList: theNightlyArticleCardListTheme(this),
            nightlyAppDLCard: theNightlyAppDLCardTheme,
            mars: theNightlyMarsTheme(this),
            bioBlock: theNightlyBioBlockTheme(this),
            profileEmailButton: theNightlyProfileEmailButtonTheme(this),
            landscape: theNightlyLandscapeTheme(this),
            taglinkNav: theNightlyTagLinkNavigationTheme(this),
            newsletter: theNightlyNewsletterTheme(this),
            pagination: theNightlyPaginationTheme(this),
            stickyAccountBanner: theNightlyStickyAccountBannerTheme(this),
            trendingArticles: theNightlyTrendingArticlesTheme(this),
            notificationCentre: theNightlyNotificationCentreTheme(this),
            electionTheme: defaultElectionWidgetTheme(this),
            electionTppEstCardTheme: theNightlyElectionTppEstCardTheme(this),
        }
    },
}

export const themes = [thewest, perthnow, sevennews, thenightly]
